import React, { useEffect, useState, useCallback } from 'react';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { notifApi } from '@/lib/api';
import { useTranslation } from 'react-i18next';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.round((now - d) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.round(diff / 60)}m`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

export function NotificationBell() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await notifApi.list(20);
      setItems(data.items);
      setUnread(data.unread);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    await notifApi.markAllRead();
    load();
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v && unread > 0) {
      // Auto mark-all on open for a friction-free UX
      setTimeout(() => notifApi.markAllRead().then(load), 800);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notif-bell"
          className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-[#0D1B2A]" />
          {unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-bold text-[#0D1B2A]">{t('notif.title')}</div>
          {items.length > 0 && (
            <button onClick={markAll} className="text-xs text-[#1677FF] hover:underline flex items-center gap-1">
              <CheckCheck className="w-3.5 h-3.5" /> {t('notif.markAllRead')}
            </button>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              <Bell className="w-6 h-6 text-slate-300 mx-auto mb-2" />
              {t('notif.empty')}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {items.map((n) => (
                <div key={n.id} className={`px-4 py-3 ${n.read ? 'opacity-70' : 'bg-[#E6F1FF]/40'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-[#0D1B2A]">{n.title}</div>
                    <div className="text-[10px] text-slate-500 shrink-0">{formatWhen(n.created_at)}</div>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-600 leading-relaxed">{n.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
